import { doc, setDoc, getDoc, getDocs, collection, query, where, updateDoc, deleteDoc, serverTimestamp, arrayUnion, arrayRemove, Timestamp, writeBatch } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Company } from './types';
import { userService } from './userService';
import { generateCompanyId } from './idGeneratorService';

export class CompanyService {
  async createCompany(companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>): Promise<Company> {
    try {
      const customId = await generateCompanyId();
      const companyRef = doc(db, 'companies', customId);
      const company: Company = {
        ...companyData,
        id: customId,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };
      await setDoc(companyRef, company);
      return company;
    } catch (error) {
      console.error('Chyba pri vytváraní firmy:', error);
      throw new Error('Nepodarilo sa vytvoriť firmu');
    }
  }

  async getCompany(companyId: string): Promise<Company | null> {
    try {
      if (!companyId) {
        return null;
      }

      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      
      if (companyDoc.exists()) {
        const companyData = companyDoc.data() as Company;
        return companyData;
      }
      
      return null;
    } catch (error) {
      console.error('Chyba pri načítaní firmy:', error);
      return null;
    }
  }

  async getCompanyByEmail(email: string): Promise<Company | null> {
    try {
      const companiesQuery = query(
        collection(db, 'companies'),
        where('email', '==', email)
      );
      
      const querySnapshot = await getDocs(companiesQuery);
      
      if (!querySnapshot.empty) {
        const companyData = querySnapshot.docs[0].data() as Company;
        return companyData;
      }
      
      return null;
    } catch (error) {
      console.error('Chyba pri hľadaní firmy podľa emailu:', error);
      return null;
    }
  }

  async updateCompany(companyId: string, updates: Partial<Company>): Promise<void> {
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Chyba pri aktualizácii firmy:', error);
      throw new Error('Nepodarilo sa aktualizovať firmu');
    }
  }

  /**
   * Resolve the signed-in user's own company id (tenant key), the same way the
   * rest of the app does: auth uid -> user document -> companyId.
   * `userService.getUser` swallows its own errors and returns null, so a missing
   * session or user simply yields `null` here.
   */
  private async getCurrentCompanyId(): Promise<string | null> {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    const user = await userService.getUser(uid);
    return user?.companyId ?? null;
  }

  /**
   * Block another company (UGC moderation Phase 2 — one-way, company-level).
   * Adds `targetCompanyId` to the CURRENT user's own company document's
   * `blockedCompanyIds`; the exchange then hides the blocked company's offers.
   *
   * MVP enforcement is blocker-side (hide offers) plus the admin hard-suspend.
   * NOTE (out of scope — follow-up): this does NOT stop the blocked company from
   * bidding on or messaging the blocker — that needs server-side bid validation
   * which does not exist yet.
   */
  async blockCompany(targetCompanyId: string): Promise<void> {
    const myCompanyId = await this.getCurrentCompanyId();
    if (!myCompanyId) throw new Error('companies.errors.block_failed');
    if (myCompanyId === targetCompanyId) throw new Error('companies.errors.cannot_block_self');
    try {
      await updateDoc(doc(db, 'companies', myCompanyId), {
        blockedCompanyIds: arrayUnion(targetCompanyId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error blocking company:', error);
      throw new Error('companies.errors.block_failed');
    }
  }

  /** Remove `targetCompanyId` from the current user's own company block list. */
  async unblockCompany(targetCompanyId: string): Promise<void> {
    const myCompanyId = await this.getCurrentCompanyId();
    if (!myCompanyId) throw new Error('companies.errors.unblock_failed');
    try {
      await updateDoc(doc(db, 'companies', myCompanyId), {
        blockedCompanyIds: arrayRemove(targetCompanyId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error unblocking company:', error);
      throw new Error('companies.errors.unblock_failed');
    }
  }

  async deleteCompany(companyId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'companies', companyId));
    } catch (error) {
      console.error('Chyba pri mazaní firmy:', error);
      throw new Error('Nepodarilo sa vymazať firmu');
    }
  }

  /**
   * Cascade hard-delete all data belonging to a company.
   * Deletes: users, offers, bids (per offer), orders, tenders, notifications (per user),
   * invitations, transports, carriers, and finally the company document itself.
   */
  async deleteCompanyData(companyId: string): Promise<void> {
    try {
      // Helper to delete all docs from a query in batches of 500
      const deleteQueryDocs = async (q: ReturnType<typeof query>) => {
        const snapshot = await getDocs(q);
        const docs = snapshot.docs;
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          docs.slice(i, i + 500).forEach(d => batch.delete(d.ref));
          await batch.commit();
        }
        return docs;
      };

      // 1. Fetch users by companyId → collect userIds, then delete user docs
      const usersQuery = query(collection(db, 'users'), where('companyId', '==', companyId));
      const userDocs = await getDocs(usersQuery);
      const userIds = userDocs.docs.map(d => d.id);

      // 2. Fetch offers by companyId → collect offerIds, then delete offer docs
      const offersQuery = query(collection(db, 'offers'), where('companyId', '==', companyId));
      const offerDocs = await getDocs(offersQuery);
      const offerIds = offerDocs.docs.map(d => d.id);

      // 3. Delete bids for each offer (Firestore 'in' supports max 30)
      for (let i = 0; i < offerIds.length; i += 30) {
        const chunk = offerIds.slice(i, i + 30);
        const bidsQuery = query(collection(db, 'bids'), where('offerId', 'in', chunk));
        await deleteQueryDocs(bidsQuery);
      }

      // 4. Delete offer docs
      await deleteQueryDocs(offersQuery);

      // 5. Delete orders by companyId
      const ordersQuery = query(collection(db, 'orders'), where('companyId', '==', companyId));
      await deleteQueryDocs(ordersQuery);

      // 6. Delete tenders by publisherId
      const tendersQuery = query(collection(db, 'tenders'), where('publisherId', '==', companyId));
      await deleteQueryDocs(tendersQuery);

      // 7. Delete notifications for each userId (max 30 per 'in' query)
      for (let i = 0; i < userIds.length; i += 30) {
        const chunk = userIds.slice(i, i + 30);
        const notificationsQuery = query(collection(db, 'notifications'), where('userId', 'in', chunk));
        await deleteQueryDocs(notificationsQuery);
      }

      // 8. Delete invitations by companyId
      const invitationsQuery = query(collection(db, 'invitations'), where('companyId', '==', companyId));
      await deleteQueryDocs(invitationsQuery);

      // 9. Delete transports by companyId
      const transportsQuery = query(collection(db, 'transports'), where('companyId', '==', companyId));
      await deleteQueryDocs(transportsQuery);

      // 10. Delete carriers by companyId
      const carriersQuery = query(collection(db, 'carriers'), where('companyId', '==', companyId));
      await deleteQueryDocs(carriersQuery);

      // 11. Delete user docs
      await deleteQueryDocs(usersQuery);

      // 12. Finally delete the company document
      await deleteDoc(doc(db, 'companies', companyId));
    } catch (error) {
      console.error('Error deleting company data:', error);
      throw new Error('Failed to delete company data');
    }
  }

  async getAllCompanies(): Promise<Company[]> {
    try {
      const companiesQuery = query(collection(db, 'companies'));
      const querySnapshot = await getDocs(companiesQuery);
      
      const companies: Company[] = [];
      querySnapshot.forEach((doc) => {
        companies.push(doc.data() as Company);
      });
      
      return companies;
    } catch (error) {
      console.error('Chyba pri načítaní všetkých firiem:', error);
      throw new Error('Nepodarilo sa načítať firmy');
    }
  }
}

export const companyService = new CompanyService();
